import type { ICsrfTokenService } from '@flippingbook/web.utils/dist/csrf';
import { Parametrized } from '@flippingbook/web.utils/dist/service';
import type { IHttpClient } from '@flippingbook/web.utils/dist/webservice';

import type { IOptionsService } from '@/services/options';

import type { AccessTokenResponse } from './interfaces/AccessTokenResponse';
import type { AuthenticateRequest } from './interfaces/AuthenticateRequest';
import type { EndRestorePasswordRequest } from './interfaces/EndRestorePasswordRequest';
import type { LoginResponse } from './interfaces/LoginResponse';
import type { RestorePasswordRequest } from './interfaces/RestorePasswordRequest';
import type { RestorePasswordResponse } from './interfaces/RestorePasswordResponse';
import type { BasicErrorResponse } from './interfaces/ServiceResponse';
import type { TokensResponse } from './interfaces/TokensResponse';
import type { UpdateUserRequest } from './interfaces/UpdateUserRequest';

export class SsoApi extends Parametrized<SsoApi.Params> {
    getToken(params: AuthenticateRequest) {
        const apiUrl =
            this.params.optionsService.getTokenType() === 'jwt'
                ? '/api/jwt'
                : this.params.optionsService.getTokenType() === 'both'
                  ? '/api/tokens'
                  : '/api/token';
        const xCsrfToken = this.params.csrfTokenService.get();

        return this.params.httpClient.fetchUrl<BasicErrorResponse<LoginResponse>>(apiUrl, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'X-CSRF-Token': xCsrfToken,
            },
            body: params,
        });
    }

    exchangeTicket({ ticket }: { ticket: string }) {
        return this.params.httpClient.fetchUrl<BasicErrorResponse<TokensResponse>>(`/api/tokens/${ticket}`, {
            method: 'GET',
            mode: 'cors',
            credentials: 'omit',
        });
    }

    getAccessToken({ persistentToken }: { persistentToken: string }) {
        return this.params.httpClient.fetchUrl<BasicErrorResponse<AccessTokenResponse>>(`/api/token`, {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'omit',
            body: JSON.stringify({ PersistentToken: persistentToken }),
        });
    }

    createUser(params: UpdateUserRequest) {
        const xCsrfToken = this.params.csrfTokenService.get();

        return this.params.httpClient.fetchUrl<BasicErrorResponse>('/api/user', {
            method: 'PUT',
            credentials: 'include',
            headers: {
                'X-CSRF-Token': xCsrfToken,
            },
            body: params,
        });
    }

    requestResetPassword(params: RestorePasswordRequest) {
        const xCsrfToken = this.params.csrfTokenService.get();

        return this.params.httpClient.fetchUrl<BasicErrorResponse<RestorePasswordResponse>>(
            '/api/user/restore-password',
            {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'X-CSRF-Token': xCsrfToken,
                },
                body: { ...params },
            },
        );
    }

    resetPassword(params: EndRestorePasswordRequest) {
        const xCsrfToken = this.params.csrfTokenService.get();

        return this.params.httpClient.fetchUrl<BasicErrorResponse<LoginResponse>>('/api/user/restore-password', {
            method: 'PUT',
            credentials: 'include',
            headers: {
                'X-CSRF-Token': xCsrfToken,
            },
            body: { ...params },
        });
    }
}

export namespace SsoApi {
    export interface Params {
        optionsService: IOptionsService;
        httpClient: IHttpClient;
        csrfTokenService: ICsrfTokenService;
    }
}
